import { LocalizedField, SupportedLanguage } from '../../types';

/**
 * Gets the localized value for a field, falling back to English if the current language is not available.
 * @param field The localized field object
 * @param currentLang The current language code
 * @returns The localized string or the English fallback, or an empty string if neither is available.
 */
export function getLocalizedValue<T>(field: LocalizedField<T> | T | undefined, currentLang: string): T | string {
  if (!field) return '';
  if (typeof field === 'string') return field;
  if (Array.isArray(field)) return field as any; // Handle non-localized arrays
  
  const fieldObj = field as LocalizedField<T>;
  
  // Helper to safely return a value, handling accidental double-nesting
  const safeReturn = (val: any): any => {
    if (val && typeof val === 'object' && !Array.isArray(val)) {
      // If it looks like a LocalizedField, recursively extract
      if ('es' in val || 'en' in val) {
        return getLocalizedValue(val, currentLang);
      }
      // Otherwise, stringify to prevent React crashes
      return JSON.stringify(val);
    }
    return val;
  };

  // 1. Try the current language
  const currentValue = fieldObj[currentLang as SupportedLanguage];
  if (currentValue !== undefined && currentValue !== null && (typeof currentValue !== 'string' || currentValue.trim() !== '')) {
    return safeReturn(currentValue);
  }
  
  // 2. Fallback to Spanish (es)
  const spanishValue = fieldObj['es'];
  if (spanishValue !== undefined && spanishValue !== null && (typeof spanishValue !== 'string' || spanishValue.trim() !== '')) {
    return safeReturn(spanishValue);
  }
  
  // 3. Fallback to English (en)
  const englishValue = fieldObj['en'];
  if (englishValue !== undefined && englishValue !== null && (typeof englishValue !== 'string' || englishValue.trim() !== '')) {
    return safeReturn(englishValue);
  }
  
  // 4. Final fallback: return the first available translation or empty string
  const firstAvailable = Object.values(fieldObj).find(v => v !== undefined && v !== null && (typeof v !== 'string' || v.trim() !== ''));
  return firstAvailable !== undefined ? safeReturn(firstAvailable) : '';
}

/**
 * Helper for arrays (like keywords or requirements)
 */
export function getLocalizedArray<T>(field: any, currentLang: string): T[] {
  if (!field) return [];
  
  // 1. If it's already an array, return it
  if (Array.isArray(field)) return field;
  
  // 2. If it's an object, it could be a LocalizedField or a Firestore object-as-array
  if (typeof field === 'object' && field !== null) {
    const keys = Object.keys(field);
    
    // Check if it looks like a LocalizedField (has at least one language key)
    const hasLangKey = keys.some(k => ['es', 'en', 'fr', 'pt', 'it', 'de', 'nl'].includes(k));
    
    if (hasLangKey) {
      const result = getLocalizedValue(field, currentLang);
      if (Array.isArray(result)) return result;
      // If the localized result is an object, it might be an object-as-array
      if (typeof result === 'object' && result !== null) {
        return Object.values(result) as T[];
      }
      if (typeof result === 'string') {
        try {
          const parsed = JSON.parse(result);
          if (Array.isArray(parsed)) return parsed;
          if (typeof parsed === 'object' && parsed !== null) return Object.values(parsed);
        } catch (e) {
          // not JSON, just a string
        }
        return result ? [result as any] : [];
      }
      return [];
    } else {
      // It's likely a Firestore object-as-array (keys are "0", "1", etc.)
      // or just a plain object that we should treat as a collection of values
      return Object.values(field) as T[];
    }
  }
  
  return [];
}
